import React, { useState } from 'react';

import { MenuItem, MenuList, ClickAwayListener, Paper, Popper, Tooltip } from '@material-ui/core';
import ArrowDropDownOutlinedIcon from '@material-ui/icons/ArrowDropDownOutlined';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { StyledTypography, StyledBorderedPaper } from 'styles/common';

import { getRandomInt } from 'constants/helper';

import TeamTypeIcon from './TeamTypeIcon';
import TeamVisibilityIcon from './TeamVisibilityIcon';

// TODO: make this a custom component which can be styled easily
// menuItems is an array of object like: {content: "", onClick: function}
export default function IconDropdown({
  menuId,
  icon,
  menuItems,
  displayArrow = true,
  maxWidth = '20%',
  displayVisibilityTypeIcon = false,
  displayTeamTypeIcon = false,
  addSeparator = false,
  className,
  isDisabled = false,
  isGoals = false,
  maxHeight,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    if (!isDisabled) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    }
  };

  const handleClose = (action) => {
    if (typeof action === 'function') action();
    setAnchorEl(null);
  };

  const clonedIcon = React.cloneElement(icon, { anchorEl });

  return (
    <DropdownContainer isDisabled={isDisabled}>
      <div onClick={handleClick}>{clonedIcon}</div>
      {displayArrow && <ArrowDropDownOutlinedIcon onClick={handleClick} />}
      <StyledPopper
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        role={undefined}
        className={className || ''} // Ideally using the classname we should only modify the top level component
        transition
      >
        <StyledBorderedPaper
          style={{
            marginTop: '1rem',
          }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <StyledMenuList
              disablePortal
              padded={displayVisibilityTypeIcon || displayTeamTypeIcon}
              separator={addSeparator}
              id={menuId}
              maxHeight={maxHeight}
            >
              {menuItems.map((item, i) => {
                item.contentProps = item.contentProps || {};
                return item.disabled && item.disabledReason ? (
                  <Tooltip title={item.disabledReason}>
                    <StyledMenuItem
                      disabled={item.disabled}
                      key={`${menuId}-item-${i}`}
                      onClick={() => handleClose(!item.disabled && item.onClick)}
                      dense={item.dense || false}
                      $separator={item.showSeparator}
                    >
                      <StyledTypography {...item.contentProps}>
                        {item.content}
                        <TeamTypeIcon show={displayTeamTypeIcon} teamType={item.teamType} />
                        <TeamVisibilityIcon show={displayVisibilityTypeIcon} visibilityType={item.visibilityType} />
                      </StyledTypography>
                    </StyledMenuItem>
                  </Tooltip>
                ) : (
                  <StyledMenuItem
                    disabled={item.disabled}
                    key={`${menuId}-item-${i}`}
                    onClick={() =>
                      handleClose(() => {
                        if (!item.disabled) {
                          if (item && item.onClick && typeof item.onClick === 'function') {
                            item.onClick(isGoals);
                          }
                        }
                      })
                    }
                    dense={item.dense || false}
                    $separator={item.showSeparator}
                  >
                    <StyledTypography {...item.contentProps}>
                      {item.content}
                      <TeamTypeIcon show={displayTeamTypeIcon} teamType={item.teamType} />
                      <TeamVisibilityIcon show={displayVisibilityTypeIcon} visibilityType={item.visibilityType} />
                    </StyledTypography>
                  </StyledMenuItem>
                );
              })}
            </StyledMenuList>
          </ClickAwayListener>
        </StyledBorderedPaper>
      </StyledPopper>
    </DropdownContainer>
  );
}

const StyledPopper = styled(Popper)`
  max-width: ${(props) => props.maxWidth};
  li {
    white-space: inherit;
  }
  min-width: 5rem;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.isDisabled ? 'initial' : 'pointer')};
`;

const StyledMenuItem = styled(MenuItem)`
  ${(props) =>
    props.disabled &&
    css`
      pointer-events: inherit !important;
      opacity: 0.5;
    `};
  ${(props) =>
    props.$separator &&
    css`
      border-bottom: 1px solid #f1f1f1;
    `};

  ${(props) =>
    props.onClick &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.palette.background.lightBlue};
        color: ${(props) => props.theme.palette.primary.main} !important;
      }

      cursor: pointer;
    `};
`;

IconDropdown.defaultProps = {
  menuId: `simpleMenu${getRandomInt()}${getRandomInt()}`,
};

IconDropdown.propTypes = {
  menuId: PropTypes.string,
  icon: PropTypes.element.isRequired,
  menuItems: PropTypes.array,
};

const StyledPaper = styled(Paper)`
  margin-top: ${(props) => props.theme.spacing(2)}rem;
`;

const StyledMenuList = styled(MenuList)`
  ${(props) =>
    props.padded &&
    css`
      li {
        padding-right: 70px;
      }
    `};
  ${(props) =>
    props.separator &&
    css`
      li {
        &:last-child {
          border-top: 1px solid #f1f1f1;
        }
      }
    `};
  box-shadow: 3px 4px 5px 0px #0000001c;

  ${(props) =>
    props.maxHeight &&
    css`
      max-height: ${(props) => props.maxHeight ?? 'none'};
      overflow: auto;
    `}
`;
